import { useEffect, useState } from "react";
import { AdminLayout } from "./AdminLayout";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Users, UserPlus, Clock, CheckCircle2, AlertCircle, CalendarCheck, Send, ArrowRightCircle, GraduationCap, BellRing, Truck } from "lucide-react";

const CARDS = [
  { key: "total_applicants", label: "Total Applicants", icon: Users },
  { key: "new_inquiries", label: "New Inquiries", icon: UserPlus },
  { key: "pending_reviews", label: "Pending Reviews", icon: Clock },
  { key: "qualified", label: "Qualified Applicants", icon: CheckCircle2 },
  { key: "conditional_approvals", label: "Conditional Approvals", icon: AlertCircle },
  { key: "interviews_scheduled", label: "Interviews Scheduled", icon: CalendarCheck },
  { key: "approved_referrals", label: "Approved Referrals", icon: Send },
  { key: "referred", label: "Referred Applicants", icon: ArrowRightCircle },
  { key: "enrolled", label: "Enrolled Participants", icon: GraduationCap },
  { key: "follow_up", label: "Requiring Follow-up", icon: BellRing },
];

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [recent, setRecent] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/admin/applicants", { params: { page: 1, page_size: 6 } }).then((r) => setRecent(r.data.items)).catch(() => {});
    api.get("/admin/referrals").then((r) => setReferrals(r.data)).catch(() => {});
  }, []);

  const stageColor = (s) => ({
    "Referred": "bg-magenta-light text-magenta",
    "Approved — Ready to Refer": "bg-navy-light text-navy",
    "Conditional Review": "bg-amber-50 text-amber-700",
    "Pending Review": "bg-soft-gray text-dark-text/70",
    "Not Eligible": "bg-red-50 text-red-600",
  }[s] || "bg-soft-gray text-dark-text/70");

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="dashboard-metrics">
        {CARDS.map((c) => (
          <div key={c.key} className="bg-white p-5 rounded-lg border-l-4 border-magenta shadow-sm" data-testid={`metric-${c.key}`}>
            <c.icon className="text-navy/40 mb-2" size={20} />
            <p className="font-display text-3xl font-black text-navy">{metrics ? metrics[c.key] : "—"}</p>
            <p className="text-xs text-dark-text/60 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-navy/10 shadow-sm">
          <div className="flex items-center justify-between p-5 border-b border-navy/10">
            <h2 className="font-display font-bold text-navy">Recent Submissions</h2>
            <button onClick={() => navigate("/admin/applicants")} className="text-sm text-magenta font-semibold hover:underline">View all</button>
          </div>
          {recent.length === 0 ? (
            <p className="p-8 text-center text-dark-text/50 text-sm">No applicants yet.</p>
          ) : (
            <div className="divide-y divide-navy/5">
              {recent.map((a) => (
                <button key={a.applicant_id} onClick={() => navigate(`/admin/applicants/${a.applicant_id}`)}
                  className="w-full flex items-center justify-between p-4 hover:bg-navy-light/50 transition-colors text-left">
                  <div>
                    <p className="font-semibold text-navy">{a.name}</p>
                    <p className="text-xs text-dark-text/60">{a.reference_number} · {a.email}</p>
                  </div>
                  <span className="text-xs bg-navy-light text-navy px-3 py-1 rounded-full font-semibold">{a.qualification}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-navy/10 shadow-sm" data-testid="referral-tracker">
          <div className="flex items-center justify-between p-5 border-b border-navy/10">
            <h2 className="font-display font-bold text-navy">Referral Tracker</h2>
            <span className="text-xs text-dark-text/50">{referrals.length} referral{referrals.length === 1 ? "" : "s"}</span>
          </div>
          {referrals.length === 0 ? (
            <p className="p-8 text-center text-dark-text/50 text-sm">No 160 or WGU referrals yet.</p>
          ) : (
            <div className="max-h-[420px] overflow-y-auto">
              {referrals.map((r, i) => (
                <button key={`${r.applicant_id}-${r.partner}`} onClick={() => navigate(`/admin/applicants/${r.applicant_id}`)}
                  data-testid={`referral-entry-${i}`}
                  className="w-full text-left flex items-start gap-3 p-4 border-b border-navy/5 hover:bg-navy-light/40 transition-colors">
                  <span className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${r.partner === "160" ? "bg-black" : "bg-navy-light"}`}>
                    {r.partner === "160" ? <Truck size={16} className="text-white" /> : <GraduationCap size={16} className="text-navy" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-navy truncate">{r.name}</p>
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap ${stageColor(r.status)}`}>{r.status}</span>
                    </div>
                    <p className="text-xs text-dark-text/60">{r.type} · {r.reference_number}{r.score != null ? ` · Score ${r.score}` : ""}{r.certificate ? ` · ${r.certificate}` : ""}</p>
                    {r.referral_date && <p className="text-xs text-magenta mt-0.5">Referred {new Date(r.referral_date).toLocaleDateString()}{r.handler ? ` by ${r.handler}` : ""}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
